import { useEffect } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';

import { customerOrderAttachmentsApi } from '#/api/crmMgmt/customerOrder/attachments';
import { fetchCustomerOrderDetail } from '#/api/crmMgmt/customerOrder/fetchDetail';
import { ATTACHMENT_POLICY_SPREADSHEET } from '#/api/platform/attachments';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { DocumentAttachmentsCard } from '#/modules/commonComponents/attachments';
import { Button } from '#/modules/commonComponents/button';
import CustomerOrderBlock from '#/modules/crmMgmt/customerOrder/components/CustomerOrderBlock';
import { getCustomerOrderApiErrorMessage } from '#/modules/crmMgmt/customerOrder/helper/apiError';
import PageShell from '#/modules/infra/layout/PageShell';
import { customerOrderDetailQueryKey } from '#/utils/queryKeys';
import { toast } from '#/utils/toast';

const CustomerOrderDetailPage = () => {
  const { id } = useParams();

  const detailQuery = useQuery({
    queryKey: customerOrderDetailQueryKey(id),
    queryFn: () => fetchCustomerOrderDetail(id),
    enabled: Boolean(id),
  });

  useEffect(() => {
    if (!detailQuery.isError) {
      return;
    }
    toast.error(getCustomerOrderApiErrorMessage(detailQuery.error, '載入客戶訂單失敗'));
  }, [detailQuery.isError, detailQuery.error]);

  const detail = detailQuery.data?.data;
  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError;

  return (
    <PageShell variant="detail">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-4xl space-y-4">
          <div
            className={cn(
              'sticky top-0 z-20 -mx-1 flex flex-wrap items-center gap-2',
              'border-b border-slate-200 bg-canvas px-1 pb-3 pt-1',
            )}
          >
            <h1 className="page_title mb-0!">
              <span className="border-l-5 border-primary-400 pl-2.5">客戶訂單詳情</span>
            </h1>
            <Button type="button" variant="outline" asChild>
              <Link to={ROUTES.customerOrder}>返回列表</Link>
            </Button>
          </div>

          {isLoading ? (
            <p className="text-sm text-slate-500">載入中…</p>
          ) : null}

          {isError ? (
            <p className="text-sm text-destructive">
              {getCustomerOrderApiErrorMessage(detailQuery.error, '載入客戶訂單失敗')}
            </p>
          ) : null}

          {detail ? (
            <>
              <CustomerOrderBlock
                detail={detail}
                showActions
                onRefetch={() => detailQuery.refetch()}
              />
              <DocumentAttachmentsCard
                businessId={String(detail.id)}
                ownerKey="customerOrder"
                api={customerOrderAttachmentsApi}
                policy={ATTACHMENT_POLICY_SPREADSHEET}
              />
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CustomerOrderDetailPage;
